import { apiClient } from './client';
import type {
  FeaturePageActionDto,
  FeaturePageActionViewModel,
  FeaturePageDto,
  FeaturePageId,
  FeaturePageMetricDto,
  FeaturePageMetricViewModel,
  FeaturePageSectionDto,
  FeaturePageSectionViewModel,
  FeaturePageViewModel,
} from '../types/featurePage';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const FEATURE_PAGE_PATH = '/api/v1/student/features';

type FallbackFeaturePageInput = {
  id: FeaturePageId;
  title: string;
  subtitle: string;
  statusLabel: string;
  progressPercent: number;
  metricLabel: string;
  metricValue: string;
  moduleTitle: string;
  moduleDescription: string;
  nextStepLabel: string;
  primaryActionLabel?: string;
  primaryHref?: string;
};

const createFallbackFeaturePage = ({
  id,
  title,
  subtitle,
  statusLabel,
  progressPercent,
  metricLabel,
  metricValue,
  moduleTitle,
  moduleDescription,
  nextStepLabel,
  primaryActionLabel,
  primaryHref,
}: FallbackFeaturePageInput): FeaturePageDto => ({
  id,
  title,
  subtitle,
  status_label: statusLabel,
  progress_percent: progressPercent,
  metrics: [
    { id: 'status', label: metricLabel, value: metricValue, helper_text: '当前页面' },
    { id: 'migration', label: '迁移状态', value: 'TS', helper_text: 'Expo Router' },
  ],
  sections: [
    {
      id: 'surface',
      title: moduleTitle,
      description: moduleDescription,
      status_label: '已迁移',
      progress_percent: Math.min(100, progressPercent + 20),
      next_step_label: nextStepLabel,
    },
    {
      id: 'integration',
      title: '后续接入',
      description: '设计图和后端字段确认后，将在当前 TypeScript 页面继续补齐细节交互。',
      status_label: '待确认',
      progress_percent: Math.max(20, progressPercent - 20),
      next_step_label: '确认接口字段、空态文案和交互细节',
    },
  ],
  actions:
    primaryActionLabel && primaryHref
      ? [{ id: 'primary', label: primaryActionLabel, href: primaryHref, kind: 'primary' }]
      : [],
});

const fallbackFeaturePages: Record<FeaturePageId, FeaturePageDto> = {
  'homework-correction': {
    id: 'homework-correction',
    title: '作业批改',
    subtitle: '上传作业或题目图片，快速查看批改结果和订正建议。',
    status_label: '待接入批改接口',
    progress_percent: 48,
    metrics: [
      { id: 'pending', label: '待批改', value: '3', helper_text: '今日作业' },
      { id: 'accuracy', label: '订正率', value: '86%', helper_text: '近 7 天' },
    ],
    sections: [
      {
        id: 'upload',
        title: '作业图片入口',
        description: '保留拍照、相册上传和题目图片预览的页面入口。',
        status_label: '已迁移',
        progress_percent: 80,
        next_step_label: '等待后端确认图片批改字段',
      },
      {
        id: 'correction-result',
        title: '批改结果卡片',
        description: '展示正确、错误、疑似和未作答题目，后续与批改接口对齐。',
        status_label: '待接口',
        progress_percent: 35,
        next_step_label: '确认题目分段和错误原因字段',
      },
    ],
    actions: [
      { id: 'photo-search', label: '去拍照搜题', href: '/photo-search', kind: 'primary' },
    ],
  },
  'weakness-improvement': {
    id: 'weakness-improvement',
    title: '薄弱提升',
    subtitle: '围绕薄弱知识点组织专项训练和复盘任务。',
    status_label: '专项训练框架',
    progress_percent: 56,
    metrics: [
      { id: 'weak-points', label: '薄弱点', value: '6', helper_text: '待强化' },
      { id: 'mastery', label: '平均掌握', value: '64%', helper_text: '当前水平' },
    ],
    sections: [
      {
        id: 'diagnosis',
        title: '薄弱点诊断',
        description: '按学科和知识点归类低掌握度内容。',
        status_label: '已迁移',
        progress_percent: 70,
        next_step_label: '接入学情分析的薄弱知识点来源',
      },
      {
        id: 'practice-loop',
        title: '练习闭环',
        description: '通过专项练习、讲解和复盘形成提升路径。',
        status_label: '待细化',
        progress_percent: 45,
        next_step_label: '确认推荐题目和完成标准',
      },
    ],
    actions: [
      { id: 'learning-analysis', label: '查看学情', href: '/learning-analysis', kind: 'primary' },
    ],
  },
  'smart-practice': {
    id: 'smart-practice',
    title: '智能练习',
    subtitle: '根据当前掌握度和错题记录推荐练习内容。',
    status_label: '推荐入口已迁移',
    progress_percent: 52,
    metrics: [
      { id: 'recommended', label: '推荐题', value: '12', helper_text: '今日' },
      { id: 'estimated-time', label: '预计用时', value: '18 分钟', helper_text: '一组练习' },
    ],
    sections: [
      {
        id: 'recommendation',
        title: '练习推荐',
        description: '整合错题、薄弱点和今日计划生成练习入口。',
        status_label: '已迁移',
        progress_percent: 65,
        next_step_label: '确认推荐接口排序规则',
      },
      {
        id: 'feedback',
        title: '练习反馈',
        description: '完成后更新掌握度、错题状态和计划进度。',
        status_label: '待接口',
        progress_percent: 38,
        next_step_label: '确认提交答案和结果回写字段',
      },
    ],
    actions: [
      { id: 'study-plan', label: '查看学习计划', href: '/study-plan', kind: 'primary' },
    ],
  },
  'growth-trajectory': {
    id: 'growth-trajectory',
    title: '成长轨迹',
    subtitle: '查看阶段性学习变化、考试节点和能力趋势。',
    status_label: '轨迹摘要已迁移',
    progress_percent: 50,
    metrics: [
      { id: 'milestones', label: '里程碑', value: '5', helper_text: '本学期' },
      { id: 'improved', label: '提升学科', value: '3', helper_text: '近 30 天' },
    ],
    sections: [
      {
        id: 'timeline',
        title: '时间线',
        description: '保留考试、练习和阶段总结的时间线展示结构。',
        status_label: '已迁移',
        progress_percent: 62,
        next_step_label: '确认时间线节点来源',
      },
      {
        id: 'trend',
        title: '能力趋势',
        description: '展示分数、掌握度和错题消化趋势。',
        status_label: '待图表',
        progress_percent: 32,
        next_step_label: '等设计图确认趋势图样式',
      },
    ],
    actions: [
      { id: 'analysis-result', label: '查看分析报告', href: '/analysis-result', kind: 'primary' },
    ],
  },
  'analysis-result': {
    id: 'analysis-result',
    title: '分析报告',
    subtitle: '汇总单次测评、练习或批改后的表现结果。',
    status_label: '报告结构已迁移',
    progress_percent: 58,
    metrics: [
      { id: 'score', label: '本次得分', value: '86', helper_text: '示例数据' },
      { id: 'rank', label: '稳定度', value: '良好', helper_text: '综合判断' },
    ],
    sections: [
      {
        id: 'summary',
        title: '结果概览',
        description: '展示得分、正确率、用时和整体建议。',
        status_label: '已迁移',
        progress_percent: 75,
        next_step_label: '确认报告 DTO 字段',
      },
      {
        id: 'knowledge',
        title: '知识点表现',
        description: '按知识点拆解掌握情况和待提升内容。',
        status_label: '待接口',
        progress_percent: 42,
        next_step_label: '对齐后端知识点维度',
      },
    ],
    actions: [
      { id: 'subject-analysis', label: '查看学科分析', href: '/subject-analysis', kind: 'primary' },
    ],
  },
  'subject-analysis': {
    id: 'subject-analysis',
    title: '学科分析',
    subtitle: '深入查看单学科知识点表现、趋势和提升建议。',
    status_label: '学科视图已迁移',
    progress_percent: 54,
    metrics: [
      { id: 'subjects', label: '学科数', value: '3', helper_text: '重点关注' },
      { id: 'weakness', label: '待提升', value: '8', helper_text: '知识点' },
    ],
    sections: [
      {
        id: 'subject-overview',
        title: '学科概览',
        description: '展示学科掌握度、趋势和最近练习表现。',
        status_label: '已迁移',
        progress_percent: 68,
        next_step_label: '与学情接口复用掌握度字段',
      },
      {
        id: 'knowledge-map',
        title: '知识点拆解',
        description: '按章节、知识点和题型归纳薄弱原因。',
        status_label: '待设计',
        progress_percent: 36,
        next_step_label: '等设计图确认层级结构',
      },
    ],
    actions: [
      { id: 'weakness-improvement', label: '去薄弱提升', href: '/weakness-improvement', kind: 'primary' },
    ],
  },
  notes: createFallbackFeaturePage({
    id: 'notes',
    title: '学习笔记',
    subtitle: '整理错题、知识点和复盘记录。',
    statusLabel: '笔记入口已迁移',
    progressPercent: 46,
    metricLabel: '笔记数',
    metricValue: '18',
    moduleTitle: '笔记列表',
    moduleDescription: '保留学习笔记、知识点摘录和复盘记录的列表入口。',
    nextStepLabel: '确认笔记分类、标签和编辑字段',
  }),
  calculator: createFallbackFeaturePage({
    id: 'calculator',
    title: '计算器',
    subtitle: '快速完成学习中的基础计算。',
    statusLabel: '工具入口已迁移',
    progressPercent: 42,
    metricLabel: '工具状态',
    metricValue: '可用',
    moduleTitle: '计算面板',
    moduleDescription: '保留学习工具页面结构，后续补齐计算输入和历史记录。',
    nextStepLabel: '确认是否使用原生计算逻辑或 WebView 方案',
  }),
  'word-lookup': createFallbackFeaturePage({
    id: 'word-lookup',
    title: '单词查询',
    subtitle: '查询英语单词释义、例句和复习记录。',
    statusLabel: '查询入口已迁移',
    progressPercent: 44,
    metricLabel: '最近查询',
    metricValue: '9',
    moduleTitle: '查询表单',
    moduleDescription: '保留单词输入、释义结果和例句展示的页面结构。',
    nextStepLabel: '确认词典接口、音标和例句字段',
  }),
  'ancient-text-analysis': createFallbackFeaturePage({
    id: 'ancient-text-analysis',
    title: '古文解析',
    subtitle: '辅助理解文言文重点词句和翻译。',
    statusLabel: '解析入口已迁移',
    progressPercent: 44,
    metricLabel: '文本模块',
    metricValue: '2',
    moduleTitle: '文本解析',
    moduleDescription: '保留原文输入、重点词句和翻译结果的展示结构。',
    nextStepLabel: '确认古文解析接口和分段字段',
  }),
  'question-collection': createFallbackFeaturePage({
    id: 'question-collection',
    title: '题目收藏',
    subtitle: '保存需要反复查看和复盘的题目。',
    statusLabel: '收藏入口已迁移',
    progressPercent: 48,
    metricLabel: '收藏题',
    metricValue: '24',
    moduleTitle: '收藏列表',
    moduleDescription: '保留收藏题列表、学科筛选和复盘入口。',
    nextStepLabel: '确认收藏接口和题目摘要字段',
  }),
  'mock-exam': createFallbackFeaturePage({
    id: 'mock-exam',
    title: '模拟考试',
    subtitle: '按考试节奏完成整套练习并查看结果。',
    statusLabel: '考试入口已迁移',
    progressPercent: 43,
    metricLabel: '试卷',
    metricValue: '5',
    moduleTitle: '试卷入口',
    moduleDescription: '保留模拟考试列表、开始考试和结果回顾入口。',
    nextStepLabel: '确认试卷、计时和交卷接口',
  }),
  'theme-settings': createFallbackFeaturePage({
    id: 'theme-settings',
    title: '主题设置',
    subtitle: '管理界面主题和显示偏好。',
    statusLabel: '设置入口已迁移',
    progressPercent: 45,
    metricLabel: '主题项',
    metricValue: '3',
    moduleTitle: '偏好设置',
    moduleDescription: '保留主题选择、显示偏好和后续设置项承载位置。',
    nextStepLabel: '确认主题 token 与设计系统变量',
  }),
  'feature-under-development': createFallbackFeaturePage({
    id: 'feature-under-development',
    title: '功能建设中',
    subtitle: '用于承接尚未开放的功能入口。',
    statusLabel: '兜底页已迁移',
    progressPercent: 40,
    metricLabel: '入口',
    metricValue: '待开放',
    moduleTitle: '建设中提示',
    moduleDescription: '保留统一的兜底提示、返回和后续跳转能力。',
    nextStepLabel: '确认哪些入口需要隐藏或灰显',
  }),
  'search-result-list': createFallbackFeaturePage({
    id: 'search-result-list',
    title: '搜题结果',
    subtitle: '兼容旧搜题结果入口，主流程已合并到拍照搜题。',
    statusLabel: '兼容路由',
    progressPercent: 62,
    metricLabel: '主流程',
    metricValue: '拍照搜题',
    moduleTitle: '结果兼容',
    moduleDescription: '旧结果列表路由继续保留，但引导到新的拍照搜题流程。',
    nextStepLabel: '确认是否彻底移除旧结果列表路由',
    primaryActionLabel: '去拍照搜题',
    primaryHref: '/photo-search',
  }),
  'answer-detail': createFallbackFeaturePage({
    id: 'answer-detail',
    title: '答案详情',
    subtitle: '兼容旧答案详情入口，主讲解流程已合并到拍照搜题。',
    statusLabel: '兼容路由',
    progressPercent: 62,
    metricLabel: '主流程',
    metricValue: 'AI 讲解',
    moduleTitle: '详情兼容',
    moduleDescription: '旧答案详情路由继续保留，用于承接旧链接和后续深链。',
    nextStepLabel: '确认答案详情是否作为拍照搜题子状态保留',
    primaryActionLabel: '去拍照搜题',
    primaryHref: '/photo-search',
  }),
  'oral-calculation': createFallbackFeaturePage({
    id: 'oral-calculation',
    title: '口算 PK',
    subtitle: '通过限时计算训练反应速度。',
    statusLabel: '游戏入口已迁移',
    progressPercent: 41,
    metricLabel: '训练轮次',
    metricValue: '3',
    moduleTitle: '口算训练',
    moduleDescription: '保留游戏入口、规则提示和后续题目区域。',
    nextStepLabel: '迁移原口算题库和计时状态',
  }),
  'idiom-battle': createFallbackFeaturePage({
    id: 'idiom-battle',
    title: '成语 PK',
    subtitle: '用对战练习成语理解和运用。',
    statusLabel: '游戏入口已迁移',
    progressPercent: 41,
    metricLabel: '题库',
    metricValue: '成语',
    moduleTitle: '成语挑战',
    moduleDescription: '保留游戏入口、题目区和结果反馈承载位置。',
    nextStepLabel: '迁移成语题库和答题状态',
  }),
  'poetry-contest': createFallbackFeaturePage({
    id: 'poetry-contest',
    title: '诗词大赛',
    subtitle: '通过题目挑战诗词积累。',
    statusLabel: '游戏入口已迁移',
    progressPercent: 41,
    metricLabel: '题库',
    metricValue: '诗词',
    moduleTitle: '诗词挑战',
    moduleDescription: '保留诗词题目、答题反馈和结果入口。',
    nextStepLabel: '迁移诗词题库和关卡规则',
  }),
};

const safeText = (value: string | null, fallback: string): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
};

const safeNumber = (value: number | null): number => {
  if (value === null || Number.isNaN(value)) {
    return 0;
  }
  return value;
};

const clampPercent = (value: number | null): number =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const mapMetricDtoToViewModel = (
  dto: FeaturePageMetricDto
): FeaturePageMetricViewModel => ({
  id: dto.id,
  label: safeText(dto.label, '指标'),
  value: safeText(dto.value, '--'),
  helperText: safeText(dto.helper_text, '暂无说明'),
});

const mapSectionDtoToViewModel = (
  dto: FeaturePageSectionDto
): FeaturePageSectionViewModel => ({
  id: dto.id,
  title: safeText(dto.title, '功能模块'),
  description: safeText(dto.description, '暂无说明'),
  statusLabel: safeText(dto.status_label, '待确认'),
  progressPercent: clampPercent(dto.progress_percent),
  nextStepLabel: safeText(dto.next_step_label, '等待后续确认'),
});

const mapActionDtoToViewModel = (
  dto: FeaturePageActionDto
): FeaturePageActionViewModel => ({
  id: dto.id,
  label: safeText(dto.label, '查看详情'),
  href: dto.href,
  kind: dto.kind === 'secondary' ? 'secondary' : 'primary',
});

export const mapFeaturePageDtoToViewModel = (
  dto: FeaturePageDto
): FeaturePageViewModel => ({
  id: dto.id,
  title: safeText(dto.title, '学习功能'),
  subtitle: safeText(dto.subtitle, '功能信息待确认'),
  statusLabel: safeText(dto.status_label, '待确认'),
  progressPercent: clampPercent(dto.progress_percent),
  metrics: (dto.metrics ?? []).map(mapMetricDtoToViewModel),
  sections: (dto.sections ?? []).map(mapSectionDtoToViewModel),
  actions: (dto.actions ?? []).map(mapActionDtoToViewModel),
});

export const getFeaturePage = async (
  featureId: FeaturePageId
): Promise<FeaturePageViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapFeaturePageDtoToViewModel(fallbackFeaturePages[featureId]);
  }

  const response = await apiClient.get<FeaturePageDto>(`${FEATURE_PAGE_PATH}/${featureId}`);
  return mapFeaturePageDtoToViewModel(response.data);
};
